import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahhvdjoh653ivfvo00g';

const PERKS = [
  { icon: 'ri-timer-flash-line', title: '到点提醒', text: '每天 17:55 一条提醒，刚好赶上准点更新' },
  { icon: 'ri-file-list-3-line', title: '重点摘要', text: '只推当天最值得看的三条，不刷屏' },
  { icon: 'ri-discuss-line', title: '热评入口', text: '附上对应楼层，直接进评论区继续挖' },
];

export default function SubscribeSection() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      return;
    }
    formData.delete('contact_alt');

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const codeOk = parsed?.code === 'OK';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && codeOk && !isSpam) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg && !isSpam
            ? serverMsg
            : '提交没有成功，请检查内容后再试一次。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络好像不太稳，稍后再试一次吧。');
    }
  };

  return (
    <section id="subscribe" className="w-full border-t border-background-200 bg-secondary-50 py-10 md:py-16">
      <div className="mx-auto w-full max-w-[1360px] px-4 md:px-6">
        <div className="grid grid-cols-1 items-start gap-8 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8 lg:grid-cols-[1.1fr_1fr] lg:gap-12 lg:p-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-600">
              Daily Reminder
            </span>
            <h2 className="mt-2 font-heading text-xl font-black leading-tight tracking-tight text-foreground-950 md:text-3xl">
              到点提醒一声，六点不用守着刷
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              黑料六点每日准点的重点摘要会在更新后自动送到你手上。只留邮箱就行，随时可以退订。
            </p>

            <ul className="mt-6 space-y-4">
              {PERKS.map((perk) => (
                <li key={perk.title} className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${perk.icon} text-lg`} />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-950">
                      {perk.title}
                    </span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-foreground-600">
                      {perk.text}
                    </span>
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-shield-check-line text-sm text-secondary-600" />
              邮箱仅用于准点提醒，不会用于其它用途
            </div>
          </div>

          <form
            data-readdy-form
            id="hlld-subscribe-form"
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6"
          >
            <p className="font-heading text-base font-black text-foreground-950">订阅准点提醒</p>

            {status === 'success' ? (
              <div className="mt-4 flex flex-col items-center rounded-md border border-secondary-300 bg-secondary-50 px-4 py-8 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                  <i className="ri-check-line text-2xl" />
                </span>
                <p className="mt-3 font-heading text-base font-black text-foreground-950">
                  已收到，准点见
                </p>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
                  每天 17:55 会给你的邮箱发一条提醒，附当天重点摘要。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-4 cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  再订阅一个
                </button>
              </div>
            ) : (
              <>
                <div className="mt-4">
                  <label htmlFor="nickname" className="block text-xs font-medium text-foreground-700">
                    称呼
                  </label>
                  <input
                    id="nickname"
                    name="nickname"
                    type="text"
                    required
                    autoComplete="nickname"
                    placeholder="怎么称呼你"
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>

                <div className="mt-4">
                  <label htmlFor="email" className="block text-xs font-medium text-foreground-700">
                    邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>

                <div className="mt-4">
                  <label htmlFor="remind_time" className="block text-xs font-medium text-foreground-700">
                    希望几点提醒
                  </label>
                  <select
                    id="remind_time"
                    name="remind_time"
                    defaultValue="17:55"
                    className="mt-1.5 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                  >
                    <option value="17:55">17:55 · 准点前五分钟</option>
                    <option value="18:00">18:00 · 正点同步</option>
                    <option value="18:30">18:30 · 摘要整理完再发</option>
                  </select>
                </div>

                <div className="mt-4">
                  <label htmlFor="interest" className="block text-xs font-medium text-foreground-700">
                    想追哪些分类（可不填）
                  </label>
                  <input
                    id="interest"
                    name="interest"
                    type="text"
                    placeholder="综艺、剧集、电竞…"
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>

                <div className="hp-field-wrap" aria-hidden="true">
                  <label htmlFor="contact_alt">备用联系方式</label>
                  <input
                    id="contact_alt"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                {status === 'error' && (
                  <p className="mt-4 rounded-md border border-primary-300 bg-primary-50 px-3 py-2.5 text-xs leading-relaxed text-primary-700">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="mt-5 flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-notification-3-line'} />
                  {status === 'submitting' ? '正在提交…' : '订阅每日准点提醒'}
                </button>
                <p className="mt-3 text-center text-[11px] text-foreground-500">
                  提交即表示同意接收准点提醒内容
                </p>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}